import React, { useCallback, useContext, useMemo, useState } from 'react';
import styled from 'styled-components';
import Typography from 'kingpin/atoms/Typography';

import ModeBar from '../ModeBar';
import AppViewersList from '../AppViewersList';
import AppAdminsList from '../AppAdminsList';
import AccountPickerContext from '../../../../../../contexts/AccountPickerContext';
import CurrentUserContext from '../../../../../../contexts/CurrentUserContext';
import getTimeStamp from '../../../../../../getTimeStamp';
import STORE from '../../../../../../store';
import Row from '../../../../../../components/Common/Row';
import Inputs from '../../../../../../components/Inputs';
import FeatureGate, {
  FEATURE_GATES,
} from '../../../../../../components/FeatureGate';
import ContextMenu, {
  Option,
} from '../../../../../../kingpin/atoms/ContextMenu';
import RolesContext from '../../../../../../contexts/RolesContext';
import PERMISSIONS from '../../../../../../permissionDefinitions';
import ToastContext from '../../../../../../contexts/ToastContext';

export const TopBarDiv = styled.div`
  height: 80px;
  min-height: 80px;
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: space-between;
  padding: 0 24px;
  border-bottom: 1px solid var(--color-border-secondary);
`;

export const ListOuterDiv = styled.div`
  padding: 0px 24px;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  flex: 1;
`;

const PublicToggle = ({ workspace }: { workspace: V3.Workspace }) => {
  const { id: currentUserId } = useContext(CurrentUserContext);
  const { selectedAccountId } = useContext(AccountPickerContext);
  const [isLoading, setIsLoading] = useState<boolean>(false);

  const isPublic = useMemo(() => {
    return workspace.appViewersAccess.isPublic;
  }, [workspace.appViewersAccess.isPublic]);

  const onToggled = useCallback(() => {
    if (isLoading) {
      return;
    }
    setIsLoading(true);
    const newWorkspace: V3.Workspace = {
      ...workspace,
      updatedOn: getTimeStamp(),
      updatedBy: currentUserId,
      appViewersAccess: {
        ...workspace.appViewersAccess,
        isPublic: !isPublic,
      },
    };

    STORE.contentDistributions
      .getWorkspacesRef({
        accountId: selectedAccountId,
      })
      .doc(newWorkspace.id)
      .set(newWorkspace)
      .then(() => {
        setIsLoading(false);
      });
  }, [currentUserId, isLoading, isPublic, selectedAccountId, workspace]);

  return (
    <Row spaceBetween centerAlign style={{ gap: 8 }}>
      <Inputs.Toggle
        value={isPublic}
        onChange={onToggled}
        isDisabled={isLoading}
      />
      <Typography.Body type={'Label'}>Public</Typography.Body>
    </Row>
  );
};

const UnpublishOption = ({ workspace }: { workspace: V3.Workspace }) => {
  const { id: currentUserId } = useContext(CurrentUserContext);
  const { selectedAccountId } = useContext(AccountPickerContext);
  const { showToast } = useContext(ToastContext);

  const [isLoading, setIsLoading] = useState<boolean>(false);

  const onClick = useCallback(() => {
    setIsLoading(true);
    const newWorkspace: V3.Workspace = {
      ...workspace,
      isPublished: false,
      updatedBy: currentUserId,
      updatedOn: getTimeStamp(),
    };

    STORE.contentDistributions
      .getWorkspacesRef({
        accountId: selectedAccountId,
      })
      .doc(newWorkspace.id)
      .set(newWorkspace)
      .then(() => {
        setIsLoading(false);
        showToast(`${workspace.label} has been unpublished successfully!`);
      });
  }, [currentUserId, selectedAccountId, showToast, workspace]);

  return (
    <Option label={'Unpublish'} onClick={onClick} isDisabled={isLoading} />
  );
};

const WorkspaceContextMenu = ({ workspace }: { workspace: V3.Workspace }) => {
  const { id: currentUserId } = useContext(CurrentUserContext);
  const { currentPermissions } = useContext(RolesContext);

  if (!workspace.isPublished) {
    return null;
  }

  if (
    workspace.createdBy !== currentUserId &&
    !currentPermissions.includes(
      PERMISSIONS.CONTENT_ACCESS.UNPUBLISH_OTHERS_CONTENT,
    )
  ) {
    return null;
  }

  return (
    <ContextMenu>
      <UnpublishOption workspace={workspace} />
    </ContextMenu>
  );
};

const WorkspaceUsersScreen = ({
  selectedWorkspace,
}: {
  selectedWorkspace: V3.Workspace;
}) => {
  const [mode, setMode] = useState<'Users' | 'Workspace Admins'>('Users');
  const modeOptions = useMemo(() => {
    return [
      {
        label: 'Users',
        key: 'users',
        isSelected: mode === 'Users',
        onClick: () => setMode('Users'),
      },
      {
        label: 'Workspace Admins',
        key: 'Workspace Admins',
        isSelected: mode === 'Workspace Admins',
        onClick: () => setMode('Workspace Admins'),
      },
    ];
  }, [mode]);

  return (
    <>
      <TopBarDiv>
        <Typography.Header type={'H3'}>User Management</Typography.Header>
        <Row style={{ gap: 16 }}>
          <FeatureGate featureName={FEATURE_GATES.V3_PUBLIC_CONTENT}>
            <PublicToggle workspace={selectedWorkspace} />
          </FeatureGate>
          <WorkspaceContextMenu workspace={selectedWorkspace} />
        </Row>
      </TopBarDiv>
      <ModeBar modeOptions={modeOptions} />
      <ListOuterDiv>
        {mode === 'Users' && <AppViewersList app={selectedWorkspace} />}
        {mode === 'Workspace Admins' && (
          <AppAdminsList app={selectedWorkspace} />
        )}
      </ListOuterDiv>
    </>
  );
};

export default WorkspaceUsersScreen;
